import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Locale } from "../../utils";
import { getCurrentPage, getCurrentLanguage, getSelectedBlock } from "../../utils/redux/reducers/CustomPage";
import { setSelectedBlock } from "../../utils/redux/actions/CustomPage";
import { BLOCK_TYPES } from "./blockConfigs";
import "./PagePreview.scss";

const i18n = Locale.getInstance();

const PagePreview = () => {
  const dispatch = useDispatch();
  const currentPage = useSelector(getCurrentPage);
  const currentLanguage = useSelector(getCurrentLanguage);
  const selectedBlock = useSelector(getSelectedBlock);

  if (!currentPage || !currentPage.layout_data || currentPage.layout_data.length === 0) {
    return (
      <div className="page-preview-empty">
        <p>{i18n.t("customPage.noBlocksPreview")}</p>
      </div>
    );
  }

  const sortedBlocks = [...currentPage.layout_data].sort((a, b) => a.order - b.order);

  const handleBlockClick = (blockId, e) => {
    e.stopPropagation();
    dispatch(setSelectedBlock(blockId));
  };

  const renderBlock = (block) => {
    const config = block.config || {};
    const isSelected = selectedBlock?.id === block.id;

    const blockClassName = `preview-block preview-${block.type} ${isSelected ? "selected" : ""}`;

    switch (block.type) {
      case BLOCK_TYPES.BANNER:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <div className="banner-container" style={{ height: `${config.height || 200}px` }}>
              {config.items && config.items.length > 0 ? (
                <>
                  <img
                    src={config.items[0]?.image || "/placeholder.png"}
                    alt="Banner"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {config.items[0]?.link_data && (
                    <div className="banner-link-info" style={{ padding: "8px", fontSize: "12px", color: "#666", background: "rgba(255,255,255,0.9)", position: "absolute", bottom: 0, left: 0, right: 0 }}>
                      {i18n.getPropNameByLocale(config.items[0].link_data, "name") || "Linked"}
                    </div>
                  )}
                </>
              ) : (
                <div className="placeholder">Banner Image</div>
              )}
            </div>
          </div>
        );

      case BLOCK_TYPES.PRODUCT_GRID:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <h3>{config.title || "Products"}</h3>
            <div className="product-grid" style={{ gridTemplateColumns: `repeat(${config.columns || 2}, 1fr)` }}>
              {Array.from({ length: Math.min(config.limit || 4, 4) }).map((_, i) => (
                <div key={i} className="product-item">
                  <div className="product-image">Product {i + 1}</div>
                  {config.show_price && <div className="product-price">¥0.00</div>}
                </div>
              ))}
            </div>
          </div>
        );

      case BLOCK_TYPES.CATEGORY_GRID:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <h3>{config.title || "Categories"}</h3>
            <div className="category-grid" style={{ gridTemplateColumns: `repeat(${config.columns || 4}, 1fr)` }}>
              {Array.from({ length: Math.min(config.category_ids?.length || 4, 8) }).map((_, i) => (
                <div key={i} className="category-item">
                  <div className="category-icon">Cat {i + 1}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case BLOCK_TYPES.NAVIGATION:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <div className="navigation-grid" style={{ gridTemplateColumns: `repeat(${config.columns || 5}, 1fr)` }}>
              {config.icons && config.icons.length > 0 ? (
                config.icons.slice(0, config.columns || 5).map((icon, i) => (
                  <div key={i} className="nav-item">
                    <div className="nav-icon">
                      {icon.icon ? (
                        <img src={icon.icon} alt={icon.title || "Icon"} />
                      ) : (
                        <div className="placeholder-icon">Icon</div>
                      )}
                    </div>
                    <div className="nav-title">{icon.title || "Nav"}</div>
                  </div>
                ))
              ) : (
                <div className="placeholder">Navigation Icons</div>
              )}
            </div>
          </div>
        );

      case BLOCK_TYPES.VIDEO:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <h3>{config.title || "Videos"}</h3>
            <div className={`video-${config.layout || "grid"}`}>
              {Array.from({ length: Math.min(config.limit || 4, 6) }).map((_, i) => (
                <div key={i} className="video-item">
                  <div className="video-thumbnail">Video {i + 1}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case BLOCK_TYPES.ADVERTISEMENT:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            {config.image ? (
              <img
                src={config.image}
                alt="Advertisement"
                style={{ width: "100%", maxHeight: "200px", objectFit: "cover" }}
              />
            ) : (
              <div className="placeholder">Advertisement Image</div>
            )}
            {config.link_data && (
              <div className="ad-link-info" style={{ padding: "8px", fontSize: "12px", color: "#666" }}>
                {i18n.getPropNameByLocale(config.link_data, "name") || "Linked"}
              </div>
            )}
          </div>
        );

      case BLOCK_TYPES.TEXT:
        return (
          <div
            key={block.id}
            className={blockClassName}
            style={{
              backgroundColor: config.background_color || "#ffffff",
              padding: `${config.padding || 10}px`,
            }}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: config.content || "<p>Text content</p>",
              }}
            />
          </div>
        );

      case BLOCK_TYPES.FLASH_SALE:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <h3>Flash Sale</h3>
            {config.show_countdown && <div className="countdown">00:00:00</div>}
            <div className="placeholder">Flash Sale Content</div>
          </div>
        );

      default:
        return (
          <div 
            key={block.id} 
            className={blockClassName}
            onClick={(e) => handleBlockClick(block.id, e)}
          >
            <div className="placeholder">Unknown Block Type: {block.type}</div>
          </div>
        );
    }
  };

  return (
    <div className="page-preview">
      <div className="preview-viewport">
        <div className="preview-content">
          {sortedBlocks.map((block) => renderBlock(block))}
        </div>
      </div>
    </div>
  );
};

export default PagePreview;

